import React, { useState } from "react";
import { useCart } from "react-use-cart";
import { MdFavorite, MdFavoriteBorder, MdStar } from "react-icons/md";
import { Link } from "react-router-dom";
import { saveShopScrollState } from "../utils/shopScrollRestore";
import { formatPrice } from "../utils/priceFormatter";

const ProductCard = (props) => {
  let { 
    product_image, 
    price, 
    product_name, 
    id, 
    old_price,
    rating = 4.5,
    review_count,
    // Firebase field mappings
    title,
    quantity
  } = props.data;
  
  // Map Firebase fields to expected field names
  const displayName = product_name || title;
  const stockStatus = quantity > 0 ? "in_stock" : "out_of_stock";
  
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Generate random high rating (4.0 to 5.0)
  const generateRandomRating = () => {
    const random = Math.random(); // 0 to 1.0
    const rating = 4.0 + random; // 4.0 to 5.0
    return Math.min(5.0, Math.round(rating * 10) / 10); // Ensure max 5.0
  };
  
  // Generate random high review count (100 to 2000)
  const generateRandomReviewCount = () => {
    return Math.floor(100 + Math.random() * 1900); // 100 to 2000
  };
  
  // Use useState to generate random values once per component mount
  const [displayRating] = useState(() => rating || generateRandomRating());
  const [displayReviewCount] = useState(() => review_count || generateRandomReviewCount());

  const handleOpenProduct = () => {
    saveShopScrollState();
  };

  const { addItem } = useCart();

  const addToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const productToAdd = {
        id: props.data.id,
        product_name: props.data.product_name || props.data.title || props.data.name,
        product_media: props.data.product_media || props.data.product_image || [],
        price: props.data.price || props.data.cost || 0,
        quantity: 1,
        ...props.data
      };
      
      addItem(productToAdd);
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };

  const toggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const getImageSrc = () => {
    if (!product_image || !Array.isArray(product_image) || product_image.length === 0) {
      return "";
    }
    return product_image[0];
  };

  // If no old_price, use current price as the "old" (crossed-out) display price
  const displayOldPrice = old_price || price;
  const discountPercentage = old_price ? Math.round(((old_price - price) / old_price) * 100) : 0;
  
  // Calculate star rating display
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 >= 0.5;

  return (
    <div
      key={id}
      className="relative isolate bg-white rounded-lg overflow-hidden border border-gray-100"
    >
      {/* Product Image Container */}
      <Link 
        to={`/product-details/${id}`}
        onClick={handleOpenProduct}
        className="block relative overflow-hidden"
      >
        <div className="aspect-square bg-gray-50 relative">
          <img 
            src={getImageSrc()} 
            alt={displayName}
            className="w-full h-full object-cover"
          />
          
          
          {/* Discount Badge - Top Right */}
          {discountPercentage > 0 && (
            <span className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-semibold px-2 py-1 rounded-full">
              -{discountPercentage}%
            </span>
          )}
          
          {/* Favorite Icon - Bottom Right of Image */}
          <button
            onClick={toggleFavorite}
            className="absolute bottom-2 right-2 z-10"
          >
            {isFavorite ? (
              <MdFavorite className="text-orange-500 text-xl" />
            ) : (
              <MdFavoriteBorder className="text-orange-500 text-xl" />
            )}
          </button>
        </div>
      </Link>

      {/* Product Info */}
      <div className="p-3 text-left">
        {/* Official Store Badge */}
        <span className="inline-block bg-blue-500 text-white text-[10px] font-semibold px-2 py-1 rounded-full mb-2 text-left">
          Official Store
        </span>

        {/* Product Name */}
        <Link 
          to={`/product-details/${id}`}
          onClick={handleOpenProduct}
          className="block text-left"
        >
          <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 leading-tight text-left">
            {displayName}
          </h3>
        </Link>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2 text-left">
          <span className="text-lg font-bold text-gray-900">
            {formatPrice(price)}
          </span>
          <span className="text-sm text-gray-400 line-through">
            {formatPrice(displayOldPrice)}
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-2 text-left">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => {
              if (i < fullStars) {
                return <MdStar key={i} className="w-3 h-3 text-yellow-400 fill-current" />;
              } else if (i === fullStars && hasHalfStar) {
                return <MdStar key={i} className="w-3 h-3 text-yellow-400 fill-current opacity-50" />;
              } else {
                return <MdStar key={i} className="w-3 h-3 text-gray-300 fill-current" />;
              }
            })}
          </div>
          <span className="text-xs text-gray-500">
            ({displayReviewCount})
          </span>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={addToCart}
          disabled={stockStatus === "out_of_stock"}
          className="w-full bg-orange-500 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
